import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Stack } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { IPositionWithCandidates } from '@types';
import { ChoiceChip } from '@components';
import { chipSx, restingChipSx } from 'theme/chips';
import { FilterSectionTitle, offersChoice } from './CandidateFilterSections';

const CollapsedPositionCount = 3;

const shortPositionName = (name: string): string => {
  const segments = name
    .split(':')
    .map((part) => part.trim())
    .filter(Boolean);
  return segments[segments.length - 1] || name;
};

interface IPositionChipProps {
  position: IPositionWithCandidates;
  selected: boolean;
  stacked?: boolean;
  onClick: () => void;
}

const PositionChip: React.FC<IPositionChipProps> = ({
  position,
  selected,
  stacked,
  onClick,
}) => (
  <ChoiceChip selected={selected} onClick={onClick}>
    <Box
      component="span"
      sx={{
        minWidth: 0,
        maxWidth: stacked ? undefined : '14rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {shortPositionName(position.name)}
    </Box>
    <Box
      component="span"
      sx={{
        color: 'text.lightGrey',
        flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {position.candidates.length}
    </Box>
  </ChoiceChip>
);

interface IPositionFilterChipsProps {
  positions: IPositionWithCandidates[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

export const PositionFilterChips: React.FC<IPositionFilterChipsProps> = ({
  positions,
  selectedIds,
  onToggle,
}) => {
  const [showAll, setShowAll] = useState(false);

  const visible = useMemo(() => {
    if (showAll || positions.length <= CollapsedPositionCount) return positions;

    const mostActive = positions.slice(0, CollapsedPositionCount);

    return positions.filter(
      (position) =>
        mostActive.includes(position) || selectedIds.includes(position.id),
    );
  }, [positions, selectedIds, showAll]);

  if (positions.length < 2) return null;

  const hiddenCount = positions.length - visible.length;

  return (
    <Box
      role="group"
      aria-label="Filter by position"
      display="flex"
      flexWrap="wrap"
      gap={0.75}
    >
      {visible.map((position) => (
        <PositionChip
          key={position.id}
          position={position}
          selected={selectedIds.includes(position.id)}
          onClick={() => onToggle(position.id)}
        />
      ))}

      {positions.length > CollapsedPositionCount && (
        <ButtonBase
          disableRipple
          aria-expanded={showAll}
          aria-label={
            showAll
              ? 'Show fewer positions'
              : `Show ${hiddenCount} more positions`
          }
          onClick={() => setShowAll((previous) => !previous)}
          sx={{ ...chipSx, ...restingChipSx }}
        >
          {showAll ? 'Show less' : `+${hiddenCount} more`}
          <ExpandMoreRoundedIcon
            sx={{
              fontSize: '1rem',
              flexShrink: 0,
              transition: 'transform 150ms',
              transform: showAll ? 'rotate(180deg)' : 'none',
            }}
          />
        </ButtonBase>
      )}
    </Box>
  );
};

export const PositionList: React.FC<IPositionFilterChipsProps> = ({
  positions,
  selectedIds,
  onToggle,
}) => {
  const listed = positions.filter(
    (position) =>
      position.candidates.length > 0 || selectedIds.includes(position.id),
  );

  if (!offersChoice(listed.length) && !selectedIds.length) return null;

  return (
    <Stack gap={1}>
      <FilterSectionTitle>Position</FilterSectionTitle>
      <Stack role="group" aria-label="Filter by position" gap={0.75}>
        {listed.map((position) => (
          <PositionChip
            key={position.id}
            position={position}
            selected={selectedIds.includes(position.id)}
            stacked
            onClick={() => onToggle(position.id)}
          />
        ))}
      </Stack>
    </Stack>
  );
};
